/**
 * HISI-FE-WS-01: WebSocket Bridge with Ephemeral Token Auth
 *
 * Enhanced WebSocket client with:
 * - Ephemeral token authentication from /api/auth/ws-token
 * - No brand parameters (brandless room model)
 * - TTL-aware token caching
 * - Safe reauth mechanism without reconnection
 * - Fallback to polling on auth failures
 */

import { io, Socket } from 'socket.io-client';
import { joinApiPath } from './api-config';

// Debug logging toggle - only enabled in development with explicit env var
const WS_DEBUG = import.meta.env.DEV && import.meta.env.VITE_WS_DEBUG === 'true';

export interface SubscriptionManifest {
  executions: string[]; // execution IDs user can access
  stages: string[]; // stage names to subscribe to
  eventTypes: string[]; // event types to receive
  maxAge?: number; // max age of events in seconds
}

export interface WebSocketBridgeConfig {
  url: string;
  subscriptions: SubscriptionManifest;
  fallbackToPolling?: boolean;
  onAuthFailure?: (error: string) => void;
  onFallback?: (reason: string) => void;
  onSubscriptionError?: (error: string, subscriptions: string[]) => void;
}

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'auth_failed' | 'fallback';

export interface WebSocketBridgeEvents {
  'connection_status': (status: ConnectionStatus) => void;
  'auth_error': (error: string) => void;
  'subscription_ack': (subscriptions: string[]) => void;
  'subscription_error': (error: string, subscriptions: string[]) => void;
  // Pipeline events (filtered server-side)
  'pipeline_event': (event: any) => void;
  'execution_update': (data: any) => void;
  'stage_update': (data: any) => void;
}

interface EphemeralToken {
  token: string;
  expires_in: number;
  fetched_at: number;
}

export class SecureWebSocketBridge {
  private socket: Socket | null = null;
  private config: WebSocketBridgeConfig;
  private status: ConnectionStatus = 'disconnected';
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private eventHandlers = new Map<keyof WebSocketBridgeEvents, Set<Function>>();
  private reauthPromise: Promise<boolean> | null = null;
  private reauthTimeout: NodeJS.Timeout | null = null;
  private ephemeralToken: EphemeralToken | null = null;
  // Concurrency hardening
  private connectPromise: Promise<void> | null = null;
  private tokenFetchPromise: Promise<string> | null = null;

  constructor(config: WebSocketBridgeConfig) {
    this.config = config;
    this.validateConfig();
  }

  private validateConfig(): void {
    const { subscriptions } = this.config;

    if (!subscriptions.executions?.length && !subscriptions.eventTypes?.length) {
      throw new Error('WebSocket bridge requires at least executions or eventTypes in subscription manifest');
    }
  }

  /**
   * Fetch ephemeral WebSocket token from backend with singleflight protection
   */
  private async fetchEphemeralToken(): Promise<string> {
    // Check if cached token is still valid
    if (this.ephemeralToken) {
      const now = Date.now();
      const tokenAge = now - this.ephemeralToken.fetched_at;
      const tokenTTL = this.ephemeralToken.expires_in * 1000;
      const refreshThreshold = 60 * 1000; // Refresh 1 minute before expiry

      if (tokenAge < (tokenTTL - refreshThreshold)) {
        if (WS_DEBUG) {
          console.log('🎫 Using cached ephemeral token', {
            age: Math.round(tokenAge / 1000),
            ttl: this.ephemeralToken.expires_in,
            remaining: Math.round((tokenTTL - tokenAge) / 1000)
          });
        }
        return this.ephemeralToken.token;
      }
    }

    // Singleflight: if already fetching, wait for existing fetch
    if (this.tokenFetchPromise) {
      if (WS_DEBUG) console.log('🎫 Reusing in-flight token fetch');
      return this.tokenFetchPromise;
    }

    // Start new token fetch with singleflight protection
    this.tokenFetchPromise = this.doFetchToken();
    try {
      const token = await this.tokenFetchPromise;
      return token;
    } finally {
      this.tokenFetchPromise = null;
    }
  }

  private async doFetchToken(): Promise<string> {
    if (WS_DEBUG) {
      console.log('🎫 Fetching new ephemeral WebSocket token...');
    }

    try {
      const response = await fetch(joinApiPath('auth', 'ws-token'), {
        method: 'GET',
        credentials: 'include', // Cookie-based auth
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ephemeral token: ${response.status}`);
      }

      const data = await response.json();

      if (!data.token || !data.expires_in) {
        throw new Error('Invalid ephemeral token response');
      }

      // Cache the token
      this.ephemeralToken = {
        token: data.token,
        expires_in: data.expires_in,
        fetched_at: Date.now(),
      };

      if (WS_DEBUG) {
        console.log('✅ Ephemeral token cached', {
          expires_in: data.expires_in,
          // SECURITY: Never log token content, even in debug mode
          token_length: data.token.length
        });
      }

      return data.token;

    } catch (error) {
      console.error('❌ Failed to fetch ephemeral token:', error);
      throw error;
    }
  }

  /**
   * Connect with ephemeral token authentication and singleflight protection
   */
  async connect(): Promise<void> {
    if (this.socket?.connected) {
      if (WS_DEBUG) console.log('🔌 WebSocket already connected');
      return;
    }

    // Singleflight: if already connecting, wait for existing connection
    if (this.connectPromise) {
      if (WS_DEBUG) console.log('🔌 Reusing in-flight connection');
      return this.connectPromise;
    }

    // Start new connection with singleflight protection
    this.connectPromise = this.doConnect();
    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  private async doConnect(): Promise<void> {
    this.updateStatus('connecting');

    try {
      // Fetch ephemeral token
      const ephemeralToken = await this.fetchEphemeralToken();

      if (WS_DEBUG) {
        console.log('🔌 Connecting WebSocket with ephemeral auth:', {
          url: this.config.url,
          subscriptions: this.config.subscriptions
        });
      }

      // Create socket with ephemeral token authentication (no brand)
      this.socket = io(this.config.url, {
        auth: {
          token: ephemeralToken,
          // HISI-FE-WS-01: No brand parameter - brandless room model
        },
        withCredentials: true, // Include cookies for session verification
        transports: ['polling', 'websocket'], // Polling first for auth
        timeout: 15000,
        reconnection: false, // We'll handle reconnection manually
      });

      this.setupEventHandlers();

      return new Promise((resolve, reject) => {
        const connectTimeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 20000);

        this.socket!.on('connect', () => {
          clearTimeout(connectTimeout);
          this.handleConnect();
          resolve();
        });

        this.socket!.on('connect_error', (error) => {
          clearTimeout(connectTimeout);
          this.handleConnectError(error);
          reject(error);
        });
      });

    } catch (error) {
      this.handleAuthError(error instanceof Error ? error.message : 'Authentication failed');
      throw error;
    }
  }

  /**
   * Reauth with new ephemeral token without reconnection
   */
  async reauth(newToken: string): Promise<boolean> {
    if (!this.socket?.connected) {
      return false;
    }

    // Prevent concurrent reauth attempts
    if (this.reauthPromise) {
      return this.reauthPromise;
    }

    this.reauthPromise = this.doReauth(newToken);
    const result = await this.reauthPromise;
    this.reauthPromise = null;

    return result;
  }

  private async doReauth(newToken: string): Promise<boolean> {
    try {
      if (WS_DEBUG) {
        console.log('🔄 Attempting WebSocket reauth with new token...');
      }

      // Send reauth event to server
      const result = await new Promise<boolean>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Reauth timeout'));
        }, 10000);

        this.socket!.emit('reauth', { token: newToken }, (response: any) => {
          clearTimeout(timeout);
          if (response === true || response?.success) {
            resolve(true);
          } else {
            reject(new Error(response?.error || 'Reauth failed'));
          }
        });
      });

      if (result) {
        if (WS_DEBUG) {
          console.log('✅ WebSocket reauth successful');
        }

        // Update cached token
        if (this.ephemeralToken) {
          this.ephemeralToken.token = newToken;
          this.ephemeralToken.fetched_at = Date.now();
        }
      }

      return result;

    } catch (error) {
      console.error('❌ WebSocket reauth failed:', error);

      // Trigger fallback to polling
      const errorMessage = error instanceof Error ? error.message : 'Reauth failed';
      if (this.config.onFallback) {
        this.config.onFallback(`Reauth failed: ${errorMessage}`);
      }

      this.updateStatus('fallback');
      return false;
    }
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => this.handleConnect());
    this.socket.on('disconnect', (reason) => this.handleDisconnect(reason));
    this.socket.on('connect_error', (error) => this.handleConnectError(error));

    // Authentication events
    this.socket.on('auth_error', (error) => this.handleAuthError(error));
    this.socket.on('auth_failed', () => this.handleAuthFailed());

    // Subscription events
    this.socket.on('subscription_ack', (subscriptions) => {
      if (WS_DEBUG) {
        console.log('✅ Subscription ACK:', subscriptions);
      }
      this.emit('subscription_ack', subscriptions);
    });

    this.socket.on('subscription_error', (error, subscriptions) => {
      console.warn('⚠️ Subscription error:', error, subscriptions);
      this.emit('subscription_error', error, subscriptions);

      if (this.config.onSubscriptionError) {
        this.config.onSubscriptionError(error, subscriptions);
      }
    });

    // Pipeline events
    this.socket.on('pipeline_event', (event) => this.emit('pipeline_event', event));
    this.socket.on('execution_update', (data) => this.emit('execution_update', data));
    this.socket.on('stage_update', (data) => this.emit('stage_update', data));
  }

  private handleConnect(): void {
    this.updateStatus('connected');
    this.reconnectAttempts = 0;

    if (WS_DEBUG) {
      console.log('✅ WebSocket connected, sending subscription manifest...');
    }

    // Send subscription manifest (without brand)
    this.socket!.emit('subscribe', {
      executions: this.config.subscriptions.executions,
      stages: this.config.subscriptions.stages,
      eventTypes: this.config.subscriptions.eventTypes,
      maxAge: this.config.subscriptions.maxAge,
      // HISI-FE-WS-01: No brand parameter in subscription
    });
  }

  private handleDisconnect(reason: string): void {
    this.updateStatus('disconnected');

    if (WS_DEBUG) {
      console.log('🔌 WebSocket disconnected:', reason);
    }

    // Handle reconnection logic based on reason
    if (reason === 'io server disconnect' || reason === 'io client disconnect') {
      // Intentional disconnect, don't reconnect
      return;
    }

    // Attempt reconnection for network issues
    this.scheduleReconnect();
  }

  private handleConnectError(error: any): void {
    console.error('❌ WebSocket connection error:', error);

    if (error.message?.includes('auth') || error.message?.includes('401')) {
      this.handleAuthError(error.message || 'Authentication error');
    } else {
      this.scheduleReconnect();
    }
  }

  private handleAuthError(error: string): void {
    console.error('🚫 WebSocket auth error:', error);
    this.updateStatus('auth_failed');

    if (this.config.onAuthFailure) {
      this.config.onAuthFailure(error);
    }

    this.emit('auth_error', error);

    // Trigger fallback to polling
    if (this.config.fallbackToPolling && this.config.onFallback) {
      this.config.onFallback(`Authentication failed: ${error}`);
      this.updateStatus('fallback');
    }
  }

  private handleAuthFailed(): void {
    this.handleAuthError('Authentication failed');
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached');

      if (this.config.fallbackToPolling && this.config.onFallback) {
        this.config.onFallback('Max reconnection attempts reached');
        this.updateStatus('fallback');
      }
      return;
    }

    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    if (WS_DEBUG) {
      console.log(`🔄 Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);
    }

    setTimeout(() => {
      this.connect().catch(error => {
        console.error('❌ Reconnection failed:', error);
      });
    }, delay);
  }

  private updateStatus(newStatus: ConnectionStatus): void {
    if (this.status !== newStatus) {
      this.status = newStatus;
      this.emit('connection_status', newStatus);

      if (WS_DEBUG) {
        console.log(`🔌 WebSocket status: ${newStatus}`);
      }
    }
  }

  /**
   * Event emitter methods
   */
  on<K extends keyof WebSocketBridgeEvents>(event: K, handler: WebSocketBridgeEvents[K]): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  off<K extends keyof WebSocketBridgeEvents>(event: K, handler: WebSocketBridgeEvents[K]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  private emit<K extends keyof WebSocketBridgeEvents>(
    event: K,
    ...args: Parameters<WebSocketBridgeEvents[K]>
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          (handler as any)(...args);
        } catch (error) {
          console.error(`Error in ${event} handler:`, error);
        }
      });
    }
  }

  /**
   * Disconnect and cleanup with complete resource cleanup
   */
  disconnect(): void {
    // Clear all timers
    if (this.reauthTimeout) {
      clearTimeout(this.reauthTimeout);
      this.reauthTimeout = null;
    }

    // Cancel in-flight promises (they will reject gracefully)
    this.connectPromise = null;
    this.tokenFetchPromise = null;
    this.reauthPromise = null;

    // Disconnect socket
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    // Clear cached token
    this.ephemeralToken = null;

    // Reset connection state
    this.reconnectAttempts = 0;
    this.updateStatus('disconnected');
    this.eventHandlers.clear();

    if (WS_DEBUG) {
      console.log('🔌 WebSocket bridge fully disconnected and cleaned up');
    }
  }

  /**
   * Getters
   */
  get connectionStatus(): ConnectionStatus {
    return this.status;
  }

  get isConnected(): boolean {
    return this.status === 'connected' && this.socket?.connected === true;
  }
}